<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { dateHelpers, LoadingView, RawSfError } from '@hfc/ui-core';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { UUID } from '@hfc/common/src/types/UUID';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import GoogleRecaptcha from '@/shared/infrastructure/components/GoogleRecaptcha.vue';
import { APPLICATION_TYPES } from '@hfc/common';
import { ChallengeQuestionFields, ChallengeType } from '@/application/shared/domain/ChallengeQuestionSettings';
import { OtpChannel } from '@/shared/domain/OtpChannel';
import ChannelSelection from '@/shared/infrastructure/components/ChannelSelection.vue';
import ChallengeForm from '@/shared/infrastructure/components/ChallengeForm.vue';
import { ChallengeQuestion } from '@/application/shared/domain/ChallengeQuestion';
import CodeVerification from '@/shared/infrastructure/components/CodeVerification.vue';
import { OtpChannelType } from '@/shared/domain/OtpChannelType';
import { AxiosError } from 'axios';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';

const { appStore, appState } = useAppStore();

const { fromUnixSecondsElapsed } = dateHelpers();
const { handleSfApiError } = useHandleApiError();

const props = defineProps<{
  applicationType: APPLICATION_TYPES;
  challengeType: ChallengeType;
  uuid: UUID;
  otpFlow?: boolean;
  subtitle?: string;
  buttonText?: string;
  localConfig: Partial<ChallengeQuestionFields>|false;
}>();

interface EmitsInterface {
  (event: 'challengeSuccess'): void
}

const emits = defineEmits<EmitsInterface>();

const formConfig = ref<ChallengeQuestionFields>();
const otpChannels = ref<Array<OtpChannel>>([]);
const showChannelSelection = ref(false);
const selectedChannel = ref<OtpChannelType | null>(null);
const captchaLoaded = ref(false);
const loadingCaptcha = ref(false);
const resend = ref(false);

const recaptchaRef = ref<InstanceType<typeof GoogleRecaptcha> | null>(null);
const channelSelectionRef = ref<InstanceType<typeof ChannelSelection> | null>(null);
const codeVerificationRef = ref<InstanceType<typeof CodeVerification> | null>(null);

const loadChallengeSettings = async <T>(
  request: () => Promise<{ success: boolean; data?: T; error?: AxiosError<RawSfError> }>
): Promise<T | undefined> => {
  const response = await request();
  if (!response.success && response.error) {
    handleSfApiError(response.error, true);

    return undefined;
  }

  return response.success ? response.data : undefined;
};

const getOtpChallengeSettings = async (): Promise<void> => {
  const data = await loadChallengeSettings(() => appStore.challengeQuestionSettingsOtp(props.applicationType, props.challengeType, props.uuid));
  if (!data) {
    return;
  }

  if (data.otp && data.otp.enabled) {
    showChannelSelection.value = data.otp.enabled;
    otpChannels.value = data.otp.channels;

    return;
  }
  formConfig.value = data.questions.fields;
};

const getBasicChallengeSettings = async (): Promise<void> => {
  const data = await loadChallengeSettings(() => appStore.challengeQuestionSettings(props.applicationType, props.challengeType, props.uuid));
  if (!data) {
    return;
  }
  formConfig.value = data;
};

const getRemoteConfig = async (): Promise<void> => {
  if (props.otpFlow) {
    await getOtpChallengeSettings();

    return;
  }

  await getBasicChallengeSettings();
};

const selectAndSendChannel = async (selectedChannelData: OtpChannelType): Promise<void> => {
  selectedChannel.value = selectedChannelData;

  await sendSelectedChannel();
};

const reSendSelectedChannel = async (): Promise<void> => {
  resend.value = true;
  await beforeSubmit();
};

const sendSelectedChannel = async (): Promise<void> => {
  if (!selectedChannel.value) {
    return;
  }
  const response = await appStore.sendSelectedChannel(props.applicationType, props.challengeType, props.uuid, selectedChannel.value);

  if (!response.success && response.error) {
    return handleSfApiError(response.error, true);
  }
  showChannelSelection.value = false;
  setLocalStorageFlag();
};

const backToChannelSelection = async (): Promise<void> => {
  if (otpChannels.value.length === 0) {
    await getRemoteConfig();
  }
  showChannelSelection.value = true;
};

const onSubmit = async (params: ChallengeQuestion): Promise<void> => {
  const { success, error } = await appStore.challengeQuestions(params, props.applicationType, props.challengeType, props.uuid);

  if (error) {
    return handleSfApiError(error, true);
  }

  if (success) {
    emits('challengeSuccess');
  }
};

const setLocalConfig = (): void => {
  formConfig.value = { ...{
    zip_code: false,
    date_of_birth: false,
    ssn: false,
    email: false,
  }, ...props.localConfig };
};

const setLocalStorageFlag = (): void => {
  const date = new Date();
  const unixSeconds = Math.floor(date.getTime() / 1000);
  localStorage.setItem('challengeVerificationCodeSent', JSON.stringify({ timestamp: unixSeconds, selectedChannelData: selectedChannel.value }));
};

const beforeSubmit = async (): Promise<void> => {
  loadingCaptcha.value = true;
  const result = await recaptchaRef.value?.executeRecaptcha();
  if (!result) {
    loadingCaptcha.value = false;
  }
};

const onCaptchaSuccess = (token: string | null): void => {
  loadingCaptcha.value = false;
  if (token) {
    sessionStorage.setItem('recaptchaToken', token);

    if (resend.value) {
      sendSelectedChannel();
      resend.value = false;

      return;
    }

    if (showChannelSelection.value) {
      channelSelectionRef.value?.onSubmit();

      return;
    }

    codeVerificationRef.value?.onSubmit();
  }
};

onMounted(async () => {
  recaptchaRef.value?.renderRecaptcha();
  const localStorageData = localStorage.getItem('challengeVerificationCodeSent');
  if (localStorageData) {
    const { selectedChannelData, timestamp } = JSON.parse(localStorageData);
    const elapsed = Math.floor(fromUnixSecondsElapsed(timestamp));
    if (elapsed < 60 && selectedChannelData) {
      selectedChannel.value = selectedChannelData;
      showChannelSelection.value = false;

      return;
    }
    localStorage.removeItem('challengeVerificationCodeSent');
  }

  if (props.localConfig) {
    setLocalConfig();

    return;
  }

  await getRemoteConfig();
});
</script>
<template>
  <div v-if="subtitle">
    <h3 class="text-center mb-4">{{ subtitle }}</h3>
  </div>
  <div v-show="appState.loading">
    <BaseSection>
      <div class="h-72 py-4 px-16 animate-pulse flex space-x-4">
        <LoadingView class="animate-pulse" />
      </div>
    </BaseSection>
  </div>
  <div v-show="!appState.loading">
    <ChannelSelection v-if="otpFlow && showChannelSelection" :loading="appState.loading || loadingCaptcha" :otp-channels="otpChannels" @channel-selected="selectAndSendChannel" @before-submit="beforeSubmit" ref="channelSelectionRef" />
    <CodeVerification v-else-if="otpFlow && !showChannelSelection && selectedChannel" :application-type="applicationType" :challenge-type="challengeType" :uuid="uuid" :selected-channel="selectedChannel" @challenge-success="emits('challengeSuccess')" @resend-verification-code="reSendSelectedChannel" @back="backToChannelSelection" @before-submit="beforeSubmit" ref="codeVerificationRef" :loading="appState.loading || loadingCaptcha" />
    <ChallengeForm v-else-if="!otpFlow && formConfig" :application-type="applicationType" :button-text="buttonText" :form-config="formConfig" :loading="appState.loading" @submit="onSubmit" @back="showChannelSelection = true" />
    <BaseSection v-else class="flex justify-center text-center h-96">
      <div class="text-2xl text-slate-600">Unable to load challenge questions. Please try again later.</div>
    </BaseSection>
    <GoogleRecaptcha container-id="recaptcha-container-challenge-questions" @captcha-success="onCaptchaSuccess"  ref="recaptchaRef" @captcha-loaded="captchaLoaded = true" />
  </div>
</template>
